/**
 * Reports & Accounts: the payroll cost dashboard, the registers, the general-ledger journal, bank
 * reconciliation, the audit trail, and the period close.
 *
 * EVERY FIGURE HERE IS A PROJECTION OF A PERSISTED RESULT. Nothing on this router runs the payroll
 * engine. The dashboard sums `PayrollEmployeeResult` rows for the run the caller is looking at, the
 * journal is built from those same rows, and the variance comes from the runs module. A reporting
 * layer that recalculates is a second implementation of payroll that will, eventually, disagree
 * with the first — and the disagreement will surface in front of a finance team.
 *
 * THE MANAGER VIEW IS A NARROWER RESPONSE, NOT A FILTERED ONE. A manager holding
 * `payroll.report.team:read` at `reporting_tree` scope gets `scope: 'reporting_tree'` and a body in
 * which the statutory balances, the executive band figures and the ledger sections are ABSENT —
 * the server does not send them. The prototype showed these as blurred-out cards, which is a
 * client-side concealment of data that was on the wire; here they are simply not on the wire, and
 * `omittedSections` says what was left out so the screen can explain itself honestly.
 *
 * THE BUDGET IS DATA, NOT AN ASSUMPTION. A department with no `PayrollDepartmentBudget` row comes
 * back with `budgetAmount: null`, and the chart says no budget is set. Inventing one — last month's
 * actual, an org average — would produce a variance bar that means nothing.
 *
 * POSTING IS PREPARED, INSPECTED, THEN POSTED. `prepareGlPosting` builds and balances the batch;
 * `postGlPosting` records that an ERP accepted it, with a reference. Nothing here integrates with
 * SAP or NetSuite: the connector name and the external reference are recorded, the file is
 * exported, and "posted" means a human or an integration told us it landed.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  CURRENCIES,
  PAYROLL_GL_ACCOUNT_GROUPS,
  PAYROLL_GL_POSTING_STATES,
  PAYROLL_PERIOD_CLOSE_STATES,
  PAYROLL_REPORT_KINDS,
} from '../enums.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const glAccountGroupSchema = z.enum(PAYROLL_GL_ACCOUNT_GROUPS);
export const glPostingStateSchema = z.enum(PAYROLL_GL_POSTING_STATES);
export const periodCloseStateSchema = z.enum(PAYROLL_PERIOD_CLOSE_STATES);
export const payrollReportKindSchema = z.enum(PAYROLL_REPORT_KINDS);
const currencySchema = z.enum(CURRENCIES);

/**
 * How much of the org the caller is being shown. Returned on every response that varies by it, so
 * the screen states its own limits rather than implying it is showing everything.
 */
export const reportScopeSchema = z.enum(['org', 'legal_entity', 'reporting_tree']);
export type ReportScope = z.infer<typeof reportScopeSchema>;

// ---------------------------------------------------------------------------
// The dashboard
// ---------------------------------------------------------------------------

export const departmentAllocationSchema = z.object({
  departmentId: uuidSchema.nullable(),
  departmentName: z.string(),
  headcount: z.number().int().nonnegative(),
  actualAmount: decimalStringSchema,
  /** Null when no budget has been set for this department and period. Never a substitute figure. */
  budgetAmount: decimalStringSchema.nullable(),
  /** Actual minus budget. Null exactly when `budgetAmount` is. */
  varianceAmount: decimalStringSchema.nullable(),
  variancePercent: decimalStringSchema.nullable(),
  /** Overtime hours clocked in the period, where attendance recorded them — the usual explanation. */
  overtimeHours: decimalStringSchema.nullable(),
});
export type DepartmentAllocation = z.infer<typeof departmentAllocationSchema>;

export const dashboardTileSchema = z.object({
  totalPayroll: decimalStringSchema,
  /** Against the prior finalised run. Null when there is no prior run to compare against. */
  totalPayrollDeltaPercent: decimalStringSchema.nullable(),
  budgetAmount: decimalStringSchema.nullable(),
  budgetVariance: decimalStringSchema.nullable(),
  headcount: z.number().int().nonnegative(),
  joiners: z.number().int().nonnegative(),
  exits: z.number().int().nonnegative(),
  statutoryOverheads: decimalStringSchema,
  statutorySharePercent: decimalStringSchema,
  averageMonthlyCtc: decimalStringSchema,
  medianMonthlyCtc: decimalStringSchema,
  topBandMonthlyCtc: decimalStringSchema,
  currency: currencySchema,
});

export const companyDashboardSchema = z.object({
  scope: reportScopeSchema,
  /**
   * The sections deliberately not included for this caller's scope, each with a one-line reason.
   * A screen that knows WHAT it is missing can say so; one that only knows a field is undefined
   * renders a blank card.
   */
  omittedSections: z.array(z.object({ section: z.string(), reason: z.string() })),
  legalEntityId: uuidSchema.nullable(),
  legalEntityName: z.string().nullable(),
  runId: uuidSchema.nullable(),
  fiscalYear: z.number().int(),
  cycleNo: z.number().int().nullable(),
  periodLabel: z.string(),
  periodState: periodCloseStateSchema,
  /** Null when nothing is calculated for the period — stated plainly, never shown as zeroes. */
  tiles: dashboardTileSchema.nullable(),
  departmentAllocation: z.array(departmentAllocationSchema),
  /** A sentence about the largest overrun, computed — or null when nothing stands out. */
  allocationNote: z.string().nullable(),
  currency: currencySchema,
});
export type CompanyDashboard = z.infer<typeof companyDashboardSchema>;

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

/**
 * Bank batch and recovery reconciliation.
 *
 * `settledCount` COMES FROM PAYMENTS & PAYSLIPS, which is a module this codebase has not built yet.
 * Until it exists, this endpoint reports `isPaymentModuleAvailable: false` and leaves the settlement
 * figures null rather than reporting "1,244 settled (99.68%)" over a disbursement nothing recorded.
 * Saying the data does not exist yet is the feature; a confident zero would not be.
 */
export const reconciliationSchema = z.object({
  runId: uuidSchema.nullable(),
  periodLabel: z.string(),
  currency: currencySchema,
  isPaymentModuleAvailable: z.boolean(),
  payableCount: z.number().int().nonnegative(),
  payableAmount: decimalStringSchema,
  heldCount: z.number().int().nonnegative(),
  heldAmount: decimalStringSchema,
  /** Employees the run flagged as unpayable — an invalid IFSC, a missing account. */
  paymentBlockedCount: z.number().int().nonnegative(),
  settledCount: z.number().int().nonnegative().nullable(),
  settledAmount: decimalStringSchema.nullable(),
  recoveredAmount: decimalStringSchema,
  /** A checksum over the persisted result set, for spotting two identical exports. Not a signature. */
  resultChecksum: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// The journal
// ---------------------------------------------------------------------------

export const glAccountMappingSchema = z.object({
  id: uuidSchema,
  legalEntityId: uuidSchema,
  accountGroup: glAccountGroupSchema,
  accountGroupLabel: z.string(),
  accountCode: z.string(),
  accountName: z.string(),
  costCenter: z.string().nullable(),
  isActive: z.boolean(),
});
export type GlAccountMapping = z.infer<typeof glAccountMappingSchema>;

export const upsertGlMappingBodySchema = z.object({
  legalEntityId: uuidSchema,
  accountGroup: glAccountGroupSchema,
  accountCode: z.string().trim().min(1).max(64),
  accountName: z.string().trim().min(1).max(200),
  costCenter: z.string().trim().max(64).optional(),
  isActive: z.boolean().default(true),
});

export const glPostingLineSchema = z.object({
  lineNo: z.number().int().positive(),
  accountGroup: glAccountGroupSchema,
  accountGroupLabel: z.string(),
  accountCode: z.string(),
  accountName: z.string(),
  costCenter: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  debit: decimalStringSchema,
  credit: decimalStringSchema,
  narrative: z.string(),
  /**
   * Figures behind the line that move nothing and choose no account (ADR 0016): on `gross_salary_expense`, the
   * month's total per company earning derived from the tenant's own data, keyed `company:<CODE>`. Absent for every
   * other line, and for a batch with no such detail.
   */
  sourceDetail: z.record(z.string(), z.string()).nullable(),
});
export type GlPostingLine = z.infer<typeof glPostingLineSchema>;

export const glPostingSchema = z.object({
  id: uuidSchema,
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  runId: uuidSchema,
  calculationVersionId: uuidSchema,
  calculationVersionNo: z.number().int().positive(),
  fiscalYear: z.number().int(),
  cycleNo: z.number().int(),
  periodLabel: z.string(),
  postingDate: dateOnlySchema,
  state: glPostingStateSchema,
  debitTotal: decimalStringSchema,
  creditTotal: decimalStringSchema,
  isBalanced: z.boolean(),
  currency: currencySchema,
  /** Account groups this batch needed and the entity has not mapped. Non-empty means not postable. */
  unmappedGroups: z.array(glAccountGroupSchema),
  connector: z.string().nullable(),
  externalRef: z.string().nullable(),
  preparedByName: z.string().nullable(),
  preparedAt: instantSchema.nullable(),
  postedByName: z.string().nullable(),
  postedAt: instantSchema.nullable(),
  reversalReason: z.string().nullable(),
  failureReason: z.string().nullable(),
  lines: z.array(glPostingLineSchema),
  /** Whether a pre-flight file exists. The link is a separate, re-authorised call. */
  hasPreflight: z.boolean(),
  canPrepare: z.boolean(),
  canPost: z.boolean(),
  blockedReason: z.string().nullable(),
});
export type GlPosting = z.infer<typeof glPostingSchema>;

export const prepareGlPostingBodySchema = z.object({
  runId: uuidSchema,
  /** Defaults to the run's pay date. Refused when it falls inside a closed period. */
  postingDate: dateOnlySchema.optional(),
  /** One salary-expense line per department instead of one for the entity. */
  splitSalaryByDepartment: z.boolean().default(false),
});

export const postGlPostingBodySchema = z.object({
  /** "SAP S/4HANA", "NetSuite", "manual_export" — what accepted it. */
  connector: z.string().trim().min(1).max(64),
  /** The ERP's own reference for the entry. Required: "posted" with no reference is an assertion. */
  externalRef: z.string().trim().min(1).max(128),
  note: z.string().trim().max(2000).optional(),
});

export const reverseGlPostingBodySchema = z.object({
  reason: z.string().trim().min(5).max(2000),
});

// ---------------------------------------------------------------------------
// Registers & exports
// ---------------------------------------------------------------------------

export const reportDefinitionSchema = z.object({
  kind: payrollReportKindSchema,
  label: z.string(),
  description: z.string(),
  /** Whether this caller may export it — the register is salary data, the headcount roll-up is not. */
  canExport: z.boolean(),
});

export const exportReportBodySchema = z.object({
  kind: payrollReportKindSchema,
  runId: uuidSchema.optional(),
  legalEntityId: uuidSchema.optional(),
  fiscalYear: z.number().int().optional(),
  format: z.enum(['csv']).default('csv'),
});

/**
 * An export comes back as content, not as a URL.
 *
 * A REGISTER IS NOT AN OBJECT WORTH STORING. It is derived from rows that are already persisted and
 * already audited, it is different the moment anything is recalculated, and writing it to storage
 * would leave every employee's net pay sitting in a bucket accumulating copies. So it is generated
 * per request, returned inline, and never written down.
 */
export const reportExportSchema = z.object({
  kind: payrollReportKindSchema,
  fileName: z.string(),
  contentType: z.string(),
  content: z.string(),
  rowCount: z.number().int().nonnegative(),
  generatedAt: instantSchema,
});

// ---------------------------------------------------------------------------
// Audit trail
// ---------------------------------------------------------------------------

/**
 * The payroll audit trail, read from the database's own trigger-written log.
 *
 * WRITTEN BY POSTGRES TRIGGERS, NOT BY THIS APPLICATION (see prisma/sql/audit.sql). That is why it
 * can be trusted: a trail the feature code writes is a trail the feature code can forget to write.
 * What this endpoint does is read it back, filtered to the payroll tables and rendered into
 * sentences.
 *
 * VALUES ARE REDACTED AT THE SOURCE. The trigger records that a PII column changed without
 * recording what it changed to, so `beforeValue`/`afterValue` are a redaction marker for those
 * columns — not because this endpoint hides them, but because they were never captured.
 */
export const auditEntrySchema = z.object({
  id: uuidSchema,
  at: instantSchema,
  actorName: z.string().nullable(),
  actorPersonId: uuidSchema.nullable(),
  /** 'user' | 'system' | 'ai' — a scheduled job and an agent are not anonymous. */
  actorType: z.string(),
  impersonatorName: z.string().nullable(),
  action: z.string(),
  /** "Salary revision approved" — the raw action rendered for a reader. */
  actionLabel: z.string(),
  entityType: z.string(),
  entityLabel: z.string(),
  entityId: uuidSchema.nullable(),
  /** The one field whose change this row is about, where a single field stands out. */
  changedField: z.string().nullable(),
  beforeValue: z.string().nullable(),
  afterValue: z.string().nullable(),
  changedFieldCount: z.number().int().nonnegative(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const listAuditQuerySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  entityType: z.string().max(64).optional(),
  actorPersonId: uuidSchema.optional(),
  from: instantSchema.optional(),
  to: instantSchema.optional(),
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
});

export const auditListSchema = z.object({
  items: z.array(auditEntrySchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  periodLabel: z.string(),
});

// ---------------------------------------------------------------------------
// Period close
// ---------------------------------------------------------------------------

/** One thing verified before a close. `isSatisfied: false` with `isBlocking` is what stops it. */
export const periodCloseCheckSchema = z.object({
  key: z.string(),
  label: z.string(),
  isSatisfied: z.boolean(),
  isBlocking: z.boolean(),
  detail: z.string().nullable(),
});
export type PeriodCloseCheck = z.infer<typeof periodCloseCheckSchema>;

export const accountingPeriodSchema = z.object({
  id: uuidSchema.nullable(),
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  fiscalYear: z.number().int(),
  cycleNo: z.number().int(),
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  periodLabel: z.string(),
  state: periodCloseStateSchema,
  checks: z.array(periodCloseCheckSchema),
  /** Recomputed on every read, so the button reflects the world now rather than at the last close. */
  canClose: z.boolean(),
  blockedReason: z.string().nullable(),
  closedByName: z.string().nullable(),
  closedAt: instantSchema.nullable(),
  closeNote: z.string().nullable(),
  reopenedByName: z.string().nullable(),
  reopenedAt: instantSchema.nullable(),
  reopenReason: z.string().nullable(),
});
export type AccountingPeriod = z.infer<typeof accountingPeriodSchema>;

export const closePeriodBodySchema = z.object({
  legalEntityId: uuidSchema,
  fiscalYear: z.number().int(),
  cycleNo: z.number().int(),
  note: z.string().trim().max(2000).optional(),
  /**
   * Close although a NON-blocking check is unsatisfied. It cannot force past a blocking one, and
   * the override is recorded in the stored checklist — a close that quietly ignored a warning would
   * be indistinguishable from one where everything was clean.
   */
  acknowledgeWarnings: z.boolean().default(false),
});

export const reopenPeriodBodySchema = z.object({
  legalEntityId: uuidSchema,
  fiscalYear: z.number().int(),
  cycleNo: z.number().int(),
  reason: z.string().trim().min(5).max(2000),
});

// ---------------------------------------------------------------------------
// Budgets
// ---------------------------------------------------------------------------

export const departmentBudgetSchema = z.object({
  id: uuidSchema,
  legalEntityId: uuidSchema,
  departmentId: uuidSchema,
  departmentName: z.string(),
  fiscalYear: z.number().int(),
  /** 0 means the annual budget — see PayrollDepartmentBudget's model comment on the sentinel. */
  cycleNo: z.number().int().nonnegative(),
  budgetAmount: decimalStringSchema,
  currency: currencySchema,
  note: z.string().nullable(),
  updatedAt: instantSchema,
});

export const upsertBudgetBodySchema = z.object({
  legalEntityId: uuidSchema,
  departmentId: uuidSchema,
  fiscalYear: z.number().int(),
  cycleNo: z.number().int().nonnegative().default(0),
  budgetAmount: decimalStringSchema,
  currency: z.enum(CURRENCIES),
  note: z.string().trim().max(500).optional(),
});

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const payrollReportsContract = c.router(
  {
    getCompanyDashboard: {
      method: 'GET',
      path: '/reports/dashboard',
      query: z.object({
        legalEntityId: uuidSchema.optional(),
        runId: uuidSchema.optional(),
        fiscalYear: z.coerce.number().int().optional(),
        cycleNo: z.coerce.number().int().optional(),
        /**
         * Ask for the narrower view although a wider one is permitted — the "preview as a team
         * lead" toggle. It can only ever NARROW: a manager passing `org` still gets
         * `reporting_tree`, because the scope is decided from their grants, not from this field.
         */
        asScope: reportScopeSchema.optional(),
      }),
      responses: {
        200: companyDashboardSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Payroll cost, headcount, statutory share and departmental allocation for a cycle',
      // Payroll-cost tiles (total, average / median / top-band CTC) and per-department actuals are other
      // people's pay, and a one-person department is that person's (ADR 0041).
      metadata: routeMeta(null, { salaryPin: 'others' }),
    },

    getReconciliation: {
      method: 'GET',
      path: '/reports/reconciliation',
      query: z.object({
        runId: uuidSchema.optional(),
        legalEntityId: uuidSchema.optional(),
      }),
      responses: {
        200: reconciliationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Bank batch and recovery reconciliation for a run',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    listReports: {
      method: 'GET',
      path: '/reports/catalogue',
      responses: {
        200: z.object({ items: z.array(reportDefinitionSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'The registers this caller may generate',
      metadata: routeMeta(null),
    },

    exportReport: {
      method: 'POST',
      path: '/reports/export',
      body: exportReportBodySchema,
      responses: {
        200: reportExportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Generate one register from persisted results and return it inline',
      metadata: routeMeta(['payroll:read', 'report:export'], { ai: 'read', salaryPin: 'others' }),
    },

    listGlMappings: {
      method: 'GET',
      path: '/reports/gl/mappings',
      query: z.object({ legalEntityId: uuidSchema }),
      responses: {
        200: z.object({
          items: z.array(glAccountMappingSchema),
          /** Every group, mapped or not, so the screen can show the gaps as gaps. */
          missingGroups: z.array(glAccountGroupSchema),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "One entity's chart-of-accounts mapping",
      metadata: routeMeta(['payroll.gl:manage', 'payroll:read']),
    },

    upsertGlMapping: {
      method: 'POST',
      path: '/reports/gl/mappings',
      body: upsertGlMappingBodySchema,
      responses: {
        200: glAccountMappingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Map one account group to the customer’s own ledger code',
      metadata: routeMeta('payroll.gl:manage'),
    },

    getGlPosting: {
      method: 'GET',
      path: '/reports/gl/postings',
      query: z.object({ runId: uuidSchema }),
      responses: {
        200: glPostingSchema.nullable(),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A run's journal batch, or null when none has been prepared",
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    prepareGlPosting: {
      method: 'POST',
      path: '/reports/gl/postings',
      headers: idempotencyHeadersSchema,
      body: prepareGlPostingBodySchema,
      responses: {
        201: glPostingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already posted, the run is not approved, or the period is closed. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Build a balanced journal from the approved calculation version',
      metadata: routeMeta(['payroll.gl:manage', 'payroll:read'], { salaryPin: 'others' }),
    },

    downloadGlPreflight: {
      method: 'POST',
      path: '/reports/gl/postings/:postingId/preflight',
      pathParams: z.object({ postingId: uuidSchema }),
      body: z.object({}),
      responses: {
        200: reportExportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'The pre-flight XML finance inspects before posting',
      metadata: routeMeta(['payroll.gl:manage', 'payroll:read'], { salaryPin: 'others' }),
    },

    postGlPosting: {
      method: 'POST',
      path: '/reports/gl/postings/:postingId/post',
      pathParams: z.object({ postingId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: postGlPostingBodySchema,
      responses: {
        200: glPostingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Unbalanced, a group is unmapped, or it is already posted. */
        409: errorBodySchema,
      },
      summary: 'Record that the ERP accepted the batch, with its reference',
      metadata: routeMeta('payroll.gl:manage'),
    },

    reverseGlPosting: {
      method: 'POST',
      path: '/reports/gl/postings/:postingId/reverse',
      pathParams: z.object({ postingId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: reverseGlPostingBodySchema,
      responses: {
        200: glPostingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Reverse a posted batch, with a reason',
      metadata: routeMeta('payroll.gl:manage'),
    },

    listAuditTrail: {
      method: 'GET',
      path: '/reports/audit',
      query: listAuditQuerySchema,
      responses: {
        200: auditListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'The payroll audit trail, read from the trigger-written log',
      metadata: routeMeta(['audit:read', 'payroll:read'], { salaryPin: 'others' }),
    },

    getAccountingPeriod: {
      method: 'GET',
      path: '/reports/periods',
      query: z.object({
        legalEntityId: uuidSchema,
        fiscalYear: z.coerce.number().int(),
        cycleNo: z.coerce.number().int(),
      }),
      responses: {
        200: accountingPeriodSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One accounting period and its close checklist, recomputed',
      metadata: routeMeta('payroll:read'),
    },

    closePeriod: {
      method: 'POST',
      path: '/reports/periods/close',
      headers: idempotencyHeadersSchema,
      body: closePeriodBodySchema,
      responses: {
        200: accountingPeriodSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** A blocking check is unsatisfied, or the period is already closed. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Close the period, recording what was verified',
      metadata: routeMeta('payroll.period:close'),
    },

    reopenPeriod: {
      method: 'POST',
      path: '/reports/periods/reopen',
      headers: idempotencyHeadersSchema,
      body: reopenPeriodBodySchema,
      responses: {
        200: accountingPeriodSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Reopen a closed period, with a reason',
      metadata: routeMeta('payroll.period:close'),
    },

    listBudgets: {
      method: 'GET',
      path: '/reports/budgets',
      query: z.object({
        legalEntityId: uuidSchema,
        fiscalYear: z.coerce.number().int(),
        cycleNo: z.coerce.number().int().nonnegative().optional(),
      }),
      responses: {
        200: z.object({ items: z.array(departmentBudgetSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Departmental payroll budgets for a year',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    upsertBudget: {
      method: 'POST',
      path: '/reports/budgets',
      body: upsertBudgetBodySchema,
      responses: {
        200: departmentBudgetSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Set one department's payroll budget",
      metadata: routeMeta(['payroll.gl:manage', 'payroll.period:close']),
    },
  },
  { pathPrefix: '/payroll' },
);
